import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import {
  CurrencyCode,
  getUserCurrency,
  setUserCurrency,
  CURRENCIES,
  formatPrice as formatPriceUtil,
  formatPriceWithIndicator,
  FX_DISCLAIMER,
  FX_DISCLAIMER_SHORT,
  requiresFxDisclaimer,
} from '../utils/currency'

interface CurrencyContextType {
  currency: CurrencyCode
  setCurrency: (currency: CurrencyCode) => void
  symbol: string
  /** Format price using Intl.NumberFormat */
  formatPrice: (amountUSD: number, showDecimals?: boolean) => string
  /** Format price with ~ indicator for non-USD currencies */
  formatPriceApprox: (amountUSD: number, showDecimals?: boolean) => string
  /** Whether current currency requires FX disclaimer */
  showFxDisclaimer: boolean
  /** Full FX disclaimer text */
  fxDisclaimer: string
  /** Short FX disclaimer for compact displays */
  fxDisclaimerShort: string
}

const CurrencyContext = createContext<CurrencyContextType | null>(null)

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>('USD')

  useEffect(() => {
    // Detect or load currency on mount
    setCurrencyState(getUserCurrency())
  }, [])

  const setCurrency = (newCurrency: CurrencyCode) => {
    setCurrencyState(newCurrency)
    setUserCurrency(newCurrency)
  }

  const config = CURRENCIES[currency]

  // Use Intl.NumberFormat-based formatting
  const formatPrice = (amountUSD: number, showDecimals = false): string => {
    return formatPriceUtil(amountUSD, currency, showDecimals)
  }

  const formatPriceApprox = (amountUSD: number, showDecimals = false): string => {
    return formatPriceWithIndicator(amountUSD, currency, showDecimals)
  }

  const showFxDisclaimer = requiresFxDisclaimer(currency)

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        symbol: config.symbol,
        formatPrice,
        formatPriceApprox,
        showFxDisclaimer,
        fxDisclaimer: FX_DISCLAIMER,
        fxDisclaimerShort: FX_DISCLAIMER_SHORT,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  )
}

export function useCurrency() {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}
